import { computed, nextTick, ref } from "vue";
import { apiLibrary } from "@/api";
import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";
import { useApi } from "@polargold/pg-frontend-vue";
import type { AuthUserShowResponse } from "@/api/modules/authUser/types";
import { MainRoles, RoleName, SYSTEM_ROLE_NAMES } from "@/config";
import {
    BUSINESS_UNIT_FILTER_MAPPING
} from "@/api/modules/autoQ/productSearch/constants.ts";

export const useAuthUserStore = defineStore("authUserStore", () => {
    const authUser = ref<AuthUserShowResponse>();
    const authUserIsReady = ref<boolean>(false);

    const isAutoQAdmin = computed(() => authUser.value?.roles.includes(RoleName.AutoQAdmin));

    const isMainRole = (roleName: string) => {
        return MainRoles.find((mainRole) => roleName === mainRole);
    };

    const mainRole = computed(() => authUser.value?.roles?.find((role) => isMainRole(role)));
    const mainRoleDisplayName = computed(() => BUSINESS_UNIT_FILTER_MAPPING[mainRole.value as RoleName]);

    const mainBusinessUnit = computed(() => authUser.value?.businessUnits[0]);

    // rethrow: false - the original swallowed the error (Sentry report + hasError, no rethrow).
    const { isLoading, hasError, callApi } = useApi({
        callback: async () => {
            const { data } = await apiLibrary.authUser.show();

            return {
                ...data,
                roles: data.roles.filter((roleName) => !SYSTEM_ROLE_NAMES.includes(roleName))
            };
        },
        onSuccess: (data) => {
            authUser.value = data;
        },
        onError: (err) => Sentry.captureException(err),
        rethrow: false,
    });

    // authUserIsReady flips true only after callApi() has fully settled and nextTick() has
    // flushed authUser's reactive update - useApi's own onSuccess isn't awaited internally, so
    // doing this inside it would let callApi() (and this fetch()) resolve before the flag
    // actually flips, which useRoutePermission's isAuthUserLoaded guard depends on being
    // accurate the moment fetch() resolves.
    const fetch = async () => {
        authUserIsReady.value = false;

        await callApi();

        if (hasError.value) return;

        await nextTick();

        authUserIsReady.value = true;
    };

    return {
        fetch,
        authUser,
        isLoading,
        hasError,
        isAutoQAdmin,
        mainRole,
        mainRoleDisplayName,
        authUserIsReady,
        mainBusinessUnit
    };
});
